/**
 * Agent OS — Agent 网络引擎
 * SPEC.md §18-21：Global AI Agent Network, Commander, Orchestrator, Registry
 * SPEC.md §8：Agent OS 公共底座，58 个 Agent
 */

import type {
  ID, AgentManifest, AgentInvocation, AgentTraceStep,
  AgentCategory, ApiResult, Evidence, AIClaim,
} from "./types";

// ─── §21 Agent Registry ────────────────────────────────────────────────

class AgentRegistry {
  private agents = new Map<ID, AgentManifest>();

  register(manifest: AgentManifest): void {
    this.agents.set(manifest.id, manifest);
  }

  get(id: ID): AgentManifest | undefined {
    return this.agents.get(id);
  }

  list(category?: AgentCategory): AgentManifest[] {
    const all = Array.from(this.agents.values());
    return category ? all.filter(a => a.category === category) : all;
  }

  listByStatus(status: AgentManifest["status"]): AgentManifest[] {
    return Array.from(this.agents.values()).filter(a => a.status === status);
  }

  search(query: string): AgentManifest[] {
    const q = query.toLowerCase();
    return Array.from(this.agents.values()).filter(a =>
      a.name.toLowerCase().includes(q) ||
      a.description.toLowerCase().includes(q) ||
      a.capabilities.some(c => c.toLowerCase().includes(q))
    );
  }
}

export const agentRegistry = new AgentRegistry();

// ─── §45 AI Memory ─────────────────────────────────────────────────────

type MemoryType = "ShortTerm" | "Session" | "User" | "Enterprise" | "Industrial";

interface MemoryEntry {
  key: string;
  value: unknown;
  type: MemoryType;
  timestamp: string;
  ttl?: number;
}

class AgentMemory {
  private memories = new Map<string, MemoryEntry>();

  set(scope: string, key: string, value: unknown, type: MemoryType = "ShortTerm"): void {
    const fullKey = `${scope}:${key}`;
    this.memories.set(fullKey, {
      key: fullKey,
      value,
      type,
      timestamp: new Date().toISOString(),
    });
  }

  get<T = unknown>(scope: string, key: string): T | undefined {
    const entry = this.memories.get(`${scope}:${key}`);
    return entry?.value as T;
  }

  getScope(scope: string): MemoryEntry[] {
    return Array.from(this.memories.values()).filter(m => m.key.startsWith(`${scope}:`));
  }

  clear(scope: string): void {
    for (const key of this.memories.keys()) {
      if (key.startsWith(`${scope}:`)) this.memories.delete(key);
    }
  }
}

export const agentMemory = new AgentMemory();

// ─── §20 Agent Orchestrator ────────────────────────────────────────────

interface Task {
  id: ID;
  agentId: ID;
  input: Record<string, unknown>;
  dependsOn?: ID[];
  status: "Pending" | "Running" | "Completed" | "Failed";
  output?: Record<string, unknown>;
}

class AgentOrchestrator {
  private invocations: AgentInvocation[] = [];

  /** §19 Global Growth Commander：用户输入目标，系统自动拆解执行 */
  async executeGoal(
    goal: string,
    context?: Record<string, unknown>,
  ): Promise<ApiResult<GoalExecutionResult>> {
    const trace: AgentTraceStep[] = [];
    const startTime = new Date().toISOString();

    try {
      trace.push({ step: "GoalParsing", input: goal, timestamp: startTime });

      const plan = this.planTasks(goal, context);
      trace.push({ step: "TaskPlanning", output: plan, timestamp: new Date().toISOString() });

      const results: Record<string, unknown> = {};
      for (const task of plan.tasks) {
        const agent = agentRegistry.get(task.agentId);
        if (!agent) {
          trace.push({ step: "AgentNotFound", input: task.agentId, timestamp: new Date().toISOString() });
          continue;
        }

        trace.push({ step: "AgentInvocation", agent: agent.id, input: task.input, timestamp: new Date().toISOString() });

        const result = await this.invokeAgent(agent, task.input, context);
        results[task.id] = result;
        task.output = result;
        task.status = "Completed";

        trace.push({ step: "AgentResult", agent: agent.id, output: result, timestamp: new Date().toISOString() });
      }

      const decision = this.aggregateResults(results, plan);
      trace.push({ step: "DecisionAggregation", output: decision, timestamp: new Date().toISOString() });

      return {
        success: true,
        data: {
          goal,
          plan,
          results,
          decision,
          trace,
          duration: Date.now() - new Date(startTime).getTime(),
        },
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : String(error),
        data: { trace } as GoalExecutionResult,
      };
    }
  }

  /** §20 Task Planning：将用户目标拆解为任务图 */
  private planTasks(goal: string, context?: Record<string, unknown>): { tasks: Task[]; strategy: string } {
    const tasks: Task[] = [];
    const goalLower = goal.toLowerCase();

    const has = (kw: string) => goalLower.includes(kw);

    if (has("进入") || has("出海") || has("市场")) {
      tasks.push({ id: "t1", agentId: "agent-market-analysis", input: { goal, context }, status: "Pending" });
      tasks.push({ id: "t2", agentId: "agent-policy-analysis", input: { goal, context }, dependsOn: ["t1"], status: "Pending" });
      tasks.push({ id: "t3", agentId: "agent-industry-chain", input: { goal, context }, dependsOn: ["t1"], status: "Pending" });
      tasks.push({ id: "t4", agentId: "agent-competitor", input: { goal, context }, dependsOn: ["t1"], status: "Pending" });
      tasks.push({ id: "t5", agentId: "agent-customer-discovery", input: { goal, context }, dependsOn: ["t1"], status: "Pending" });
      tasks.push({ id: "t6", agentId: "agent-partner-discovery", input: { goal, context }, dependsOn: ["t3"], status: "Pending" });
      tasks.push({ id: "t7", agentId: "agent-talent-discovery", input: { goal, context }, dependsOn: ["t3"], status: "Pending" });
      tasks.push({ id: "t8", agentId: "agent-strategy", input: { goal, context, results: ["t1","t2","t3","t4","t5","t6","t7"] }, dependsOn: ["t1","t2","t3","t4","t5","t6","t7"], status: "Pending" });
      tasks.push({ id: "t9", agentId: "agent-action-plan", input: { goal, context }, dependsOn: ["t8"], status: "Pending" });
      return { tasks, strategy: "MarketEntry" };
    }

    if (has("机会") || has("趋势") || has("发现")) {
      tasks.push({ id: "t1", agentId: "agent-opportunity-radar", input: { goal, context }, status: "Pending" });
      tasks.push({ id: "t2", agentId: "agent-trend-prediction", input: { goal, context }, dependsOn: ["t1"], status: "Pending" });
      tasks.push({ id: "t3", agentId: "agent-enterprise-match", input: { goal, context }, dependsOn: ["t1"], status: "Pending" });
      return { tasks, strategy: "OpportunityDiscovery" };
    }

    if (has("竞争") || has("对手")) {
      tasks.push({ id: "t1", agentId: "agent-competitor", input: { goal, context }, status: "Pending" });
      tasks.push({ id: "t2", agentId: "agent-market-analysis", input: { goal, context }, dependsOn: ["t1"], status: "Pending" });
      return { tasks, strategy: "CompetitiveAnalysis" };
    }

    if (has("人才") || has("招聘") || has("岗位")) {
      tasks.push({ id: "t1", agentId: "agent-talent-discovery", input: { goal, context }, status: "Pending" });
      tasks.push({ id: "t2", agentId: "agent-talent-match", input: { goal, context }, dependsOn: ["t1"], status: "Pending" });
      return { tasks, strategy: "TalentAcquisition" };
    }

    // 拓客 / 审计类目标（2026-09-07 新增识别，避免落入空泛的总指挥单任务，也优先于"客户/线索"旧分支）：
    // - 拓客/获客/找客户 → 真实线索链路（agent-lead-gen / enterprise-match / partner-discovery）
    // - 审计/尽调/体检 → 真实审计链路（agent-financial-analysis / risk-assessment / enterprise-match）
    // - 两者同时出现（如"深度审计拓客国内外"）→ 组合链路
    const wantsProspect = ["拓客", "获客", "找客户", "开发客户", "拓新客", "拉新", "卖货"].some((kw) => goalLower.includes(kw));
    const wantsAudit = ["审计", "尽调", "背调", "体检", "核查", "审查", "风控"].some((kw) => goalLower.includes(kw));

    if (wantsProspect && wantsAudit) {
      tasks.push({ id: "t1", agentId: "agent-lead-gen", input: { goal, context }, status: "Pending" });
      tasks.push({ id: "t2", agentId: "agent-financial-analysis", input: { goal, context }, dependsOn: ["t1"], status: "Pending" });
      tasks.push({ id: "t3", agentId: "agent-enterprise-match", input: { goal, context }, dependsOn: ["t1"], status: "Pending" });
      tasks.push({ id: "t4", agentId: "agent-risk-assessment", input: { goal, context }, dependsOn: ["t2"], status: "Pending" });
      tasks.push({ id: "t5", agentId: "agent-partner-discovery", input: { goal, context }, dependsOn: ["t3"], status: "Pending" });
      return { tasks, strategy: "ProspectingAudit" };
    }

    if (wantsProspect) {
      tasks.push({ id: "t1", agentId: "agent-lead-gen", input: { goal, context }, status: "Pending" });
      tasks.push({ id: "t2", agentId: "agent-enterprise-match", input: { goal, context }, dependsOn: ["t1"], status: "Pending" });
      tasks.push({ id: "t3", agentId: "agent-partner-discovery", input: { goal, context }, dependsOn: ["t1"], status: "Pending" });
      return { tasks, strategy: "Prospecting" };
    }

    if (wantsAudit) {
      tasks.push({ id: "t1", agentId: "agent-financial-analysis", input: { goal, context }, status: "Pending" });
      tasks.push({ id: "t2", agentId: "agent-risk-assessment", input: { goal, context }, dependsOn: ["t1"], status: "Pending" });
      tasks.push({ id: "t3", agentId: "agent-enterprise-match", input: { goal, context }, dependsOn: ["t1"], status: "Pending" });
      return { tasks, strategy: "DeepAudit" };
    }

    if (has("客户") || has("线索") || has("lead") || has("潜客")) {
      tasks.push({ id: "t1", agentId: "agent-customer-discovery", input: { goal, context }, status: "Pending" });
      tasks.push({ id: "t2", agentId: "agent-lead-gen", input: { goal, context }, dependsOn: ["t1"], status: "Pending" });
      tasks.push({ id: "t3", agentId: "agent-enterprise-match", input: { goal, context }, dependsOn: ["t1"], status: "Pending" });
      return { tasks, strategy: "CustomerDiscovery" };
    }

    if (has("教育") || has("课程") || has("高校") || has("技能")) {
      tasks.push({ id: "t1", agentId: "agent-skill-gap", input: { goal, context }, status: "Pending" });
      tasks.push({ id: "t2", agentId: "agent-course-recommend", input: { goal, context }, dependsOn: ["t1"], status: "Pending" });
      tasks.push({ id: "t3", agentId: "agent-university-match", input: { goal, context }, dependsOn: ["t1"], status: "Pending" });
      return { tasks, strategy: "EducationTech" };
    }

    if (has("生态") || has("互益") || has("修复")) {
      tasks.push({ id: "t1", agentId: "agent-ecosystem-match", input: { goal, context }, status: "Pending" });
      tasks.push({ id: "t2", agentId: "agent-ecosystem-recovery", input: { goal, context }, dependsOn: ["t1"], status: "Pending" });
      return { tasks, strategy: "EcosystemRecovery" };
    }

    tasks.push({ id: "t1", agentId: "agent-growth-commander", input: { goal, context }, status: "Pending" });
    return { tasks, strategy: "General" };
  }

  private async invokeAgent(
    agent: AgentManifest,
    input: Record<string, unknown>,
    context?: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    const invocation: AgentInvocation = {
      id: `inv-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      agentId: agent.id,
      input,
      status: "Running",
      startedAt: new Date().toISOString(),
    };
    this.invocations.push(invocation);

    try {
      const handler = agentHandlers.get(agent.id);
      if (handler) {
        const result = await handler(input, context || {});
        invocation.output = result;
        invocation.status = "Success";
        invocation.completedAt = new Date().toISOString();
        return result;
      }

      const fallback = {
        agent: agent.name,
        status: "completed",
        message: `Agent ${agent.name} executed with input`,
        input,
        timestamp: new Date().toISOString(),
      };
      invocation.output = fallback;
      invocation.status = "Success";
      invocation.completedAt = new Date().toISOString();
      return fallback;
    } catch (error) {
      invocation.status = "Failed";
      invocation.error = error instanceof Error ? error.message : String(error);
      invocation.completedAt = new Date().toISOString();
      return { error: invocation.error };
    }
  }

  private aggregateResults(results: Record<string, unknown>, plan: { tasks: Task[]; strategy: string }): Record<string, unknown> {
    return {
      strategy: plan.strategy,
      taskCount: plan.tasks.length,
      completedTasks: plan.tasks.filter(t => t.status === "Completed").length,
      results,
      summary: `完成 ${plan.tasks.length} 个 Agent 任务`,
      timestamp: new Date().toISOString(),
    };
  }

  getInvocations(): AgentInvocation[] {
    return this.invocations;
  }

  /** 供 WorkflowEngine 调用：执行单个 Agent */
  async invokeAgentPublic(
    agent: AgentManifest,
    input: Record<string, unknown>,
    context?: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.invokeAgent(agent, input, context);
  }
}

export interface GoalExecutionResult {
  goal: string;
  plan: { tasks: Task[]; strategy: string };
  results: Record<string, unknown>;
  decision: Record<string, unknown>;
  trace: AgentTraceStep[];
  duration: number;
}

export const agentOrchestrator = new AgentOrchestrator();

// ─── Agent Handler 注册 ────────────────────────────────────────────────

type AgentHandler = (
  input: Record<string, unknown>,
  context: Record<string, unknown>,
) => Promise<Record<string, unknown>>;

const agentHandlers = new Map<ID, AgentHandler>();

export function registerAgentHandler(agentId: ID, handler: AgentHandler): void {
  agentHandlers.set(agentId, handler);
}

// ─── §18 注册 58 个 Agent ──────────────────────────────────────────────

export function initializeAgents(): void {
  const agents: AgentManifest[] = [
    // 总控 (3)
    { id: "agent-growth-commander", name: "全球增长总指挥", nameEn: "Global Growth Commander", version: "1.0", description: "用户目标理解、任务拆解、Agent 调度", category: "Command", capabilities: ["goal-parsing", "task-planning", "agent-routing"], tools: ["*"], permissions: ["read:all"], inputSchema: {}, outputSchema: {}, status: "Published", prompt: "你是全球产业增长总指挥" },
    { id: "agent-orchestrator", name: "Agent 编排器", nameEn: "Agent Orchestrator", version: "1.0", description: "Agent 发现、路由、调用、聚合", category: "Command", capabilities: ["orchestration", "routing", "aggregation"], tools: ["agent-registry"], permissions: ["read:agents"], inputSchema: {}, outputSchema: {}, status: "Published" },
    { id: "agent-evaluator", name: "Agent 评估器", nameEn: "Agent Evaluator", version: "1.0", description: "Agent 性能评估、质量监控", category: "Command", capabilities: ["evaluation", "monitoring"], tools: ["metrics"], permissions: ["read:metrics"], inputSchema: {}, outputSchema: {}, status: "Published" },

    // 产业 (12)
    { id: "agent-industry-analysis", name: "产业分析 Agent", version: "1.0", description: "产业结构、趋势、竞争分析", category: "Industry", capabilities: ["industry-analysis"], tools: ["kg-query", "data-query"], permissions: ["read:industry"], inputSchema: {}, outputSchema: {}, status: "Published" },
    { id: "agent-industry-chain", name: "产业链分析 Agent", version: "1.0", description: "产业链上下游分析", category: "Industry", capabilities: ["chain-analysis"], tools: ["kg-query"], permissions: ["read:industry"], inputSchema: {}, outputSchema: {}, status: "Published" },
    { id: "agent-market-analysis", name: "市场分析 Agent", version: "1.0", description: "市场规模、增长、竞争分析", category: "Market", capabilities: ["market-analysis"], tools: ["kg-query", "data-query"], permissions: ["read:market"], inputSchema: {}, outputSchema: {}, status: "Published" },
    { id: "agent-policy-analysis", name: "政策分析 Agent", version: "1.0", description: "政策影响分析", category: "Industry", capabilities: ["policy-analysis"], tools: ["kg-query"], permissions: ["read:policy"], inputSchema: {}, outputSchema: {}, status: "Published" },
    { id: "agent-trend-prediction", name: "趋势预测 Agent", version: "1.0", description: "产业趋势预测", category: "Industry", capabilities: ["prediction"], tools: ["data-query", "ai-model"], permissions: ["read:industry"], inputSchema: {}, outputSchema: {}, status: "Published" },
    { id: "agent-opportunity-radar", name: "机会雷达 Agent", version: "1.0", description: "全球产业机会发现", category: "Opportunity", capabilities: ["opportunity-discovery"], tools: ["kg-query", "data-query", "ai-model"], permissions: ["read:all"], inputSchema: {}, outputSchema: {}, status: "Published" },
    { id: "agent-competitor", name: "竞争对手分析 Agent", version: "1.0", description: "竞争对手识别与分析", category: "Enterprise", capabilities: ["competitor-analysis"], tools: ["kg-query"], permissions: ["read:enterprise"], inputSchema: {}, outputSchema: {}, status: "Published" },
    { id: "agent-customer-discovery", name: "客户发现 Agent", version: "1.0", description: "潜在客户发现", category: "Market", capabilities: ["customer-discovery"], tools: ["kg-query", "data-query"], permissions: ["read:market"], inputSchema: {}, outputSchema: {}, status: "Published" },
    { id: "agent-partner-discovery", name: "合作伙伴发现 Agent", version: "1.0", description: "合作伙伴匹配", category: "Ecosystem", capabilities: ["partner-discovery"], tools: ["kg-query"], permissions: ["read:enterprise"], inputSchema: {}, outputSchema: {}, status: "Published" },
    { id: "agent-talent-discovery", name: "人才发现 Agent", version: "1.0", description: "人才搜索与匹配", category: "Talent", capabilities: ["talent-discovery"], tools: ["kg-query", "data-query"], permissions: ["read:talent"], inputSchema: {}, outputSchema: {}, status: "Published" },
    { id: "agent-skill-analysis", name: "技能分析 Agent", version: "1.0", description: "技能供需分析", category: "Talent", capabilities: ["skill-analysis"], tools: ["data-query"], permissions: ["read:talent"], inputSchema: {}, outputSchema: {}, status: "Published" },
    { id: "agent-enterprise-match", name: "企业匹配 Agent", version: "1.0", description: "企业与机会匹配", category: "Enterprise", capabilities: ["enterprise-matching"], tools: ["kg-query", "ai-model"], permissions: ["read:enterprise"], inputSchema: {}, outputSchema: {}, status: "Published" },
    { id: "agent-investment-analysis", name: "投资分析 Agent", version: "1.0", description: "投资机会分析", category: "Investment", capabilities: ["investment-analysis"], tools: ["kg-query", "data-query"], permissions: ["read:investment"], inputSchema: {}, outputSchema: {}, status: "Published" },

    // 企业 (8)
    { id: "agent-enterprise-growth", name: "企业增长 Agent", version: "1.0", description: "每日增长简报、机会推荐", category: "Enterprise", capabilities: ["growth-tracking", "daily-brief"], tools: ["kg-query", "data-query", "ai-model"], permissions: ["read:enterprise"], inputSchema: {}, outputSchema: {}, status: "Published" },
    { id: "agent-strategy", name: "战略生成 Agent", version: "1.0", description: "市场进入战略生成", category: "Strategy", capabilities: ["strategy-generation"], tools: ["ai-model", "kg-query"], permissions: ["read:all"], inputSchema: {}, outputSchema: {}, status: "Published" },
    { id: "agent-action-plan", name: "行动计划 Agent", version: "1.0", description: "90天行动计划生成", category: "Strategy", capabilities: ["action-planning"], tools: ["ai-model"], permissions: ["read:all"], inputSchema: {}, outputSchema: {}, status: "Published" },
    { id: "agent-risk-assessment", name: "风险评估 Agent", version: "1.0", description: "市场风险识别", category: "Enterprise", capabilities: ["risk-assessment"], tools: ["data-query"], permissions: ["read:market"], inputSchema: {}, outputSchema: {}, status: "Published" },
    { id: "agent-supply-chain", name: "供应链分析 Agent", version: "1.0", description: "供应链分析", category: "Enterprise", capabilities: ["supply-chain-analysis"], tools: ["kg-query"], permissions: ["read:enterprise"], inputSchema: {}, outputSchema: {}, status: "Published" },
    { id: "agent-financial-analysis", name: "财务分析 Agent", version: "1.0", description: "财务健康度分析", category: "Enterprise", capabilities: ["financial-analysis"], tools: ["data-query"], permissions: ["read:enterprise"], inputSchema: {}, outputSchema: {}, status: "Published" },
    { id: "agent-enterprise-profile", name: "企业画像 Agent", version: "1.0", description: "企业动态画像", category: "Enterprise", capabilities: ["profiling"], tools: ["ai-model", "kg-query"], permissions: ["read:enterprise"], inputSchema: {}, outputSchema: {}, status: "Published" },
    { id: "agent-digital-twin", name: "数字孪生 Agent", version: "1.0", description: "企业数字孪生模拟", category: "Technology", capabilities: ["simulation"], tools: ["digital-twin"], permissions: ["read:enterprise"], inputSchema: {}, outputSchema: {}, status: "Published" },

    // 教育人才 (8)
    { id: "agent-education-match", name: "教育匹配 Agent", version: "1.0", description: "高校与企业匹配", category: "Education", capabilities: ["education-matching"], tools: ["kg-query"], permissions: ["read:education"], inputSchema: {}, outputSchema: {}, status: "Published" },
    { id: "agent-course-recommend", name: "课程推荐 Agent", version: "1.0", description: "课程推荐", category: "Education", capabilities: ["course-recommendation"], tools: ["ai-model"], permissions: ["read:education"], inputSchema: {}, outputSchema: {}, status: "Published" },
    { id: "agent-talent-match", name: "人才匹配 Agent", version: "1.0", description: "人才与岗位匹配", category: "Talent", capabilities: ["talent-matching"], tools: ["ai-model", "kg-query"], permissions: ["read:talent"], inputSchema: {}, outputSchema: {}, status: "Published" },
    { id: "agent-talent-predict", name: "人才预测 Agent", version: "1.0", description: "产业人才需求预测", category: "Talent", capabilities: ["talent-prediction"], tools: ["data-query", "ai-model"], permissions: ["read:talent"], inputSchema: {}, outputSchema: {}, status: "Published" },
    { id: "agent-skill-gap", name: "技能差距 Agent", version: "1.0", description: "技能差距分析", category: "Education", capabilities: ["skill-gap-analysis"], tools: ["data-query"], permissions: ["read:talent"], inputSchema: {}, outputSchema: {}, status: "Published" },
    { id: "agent-university-match", name: "高校匹配 Agent", version: "1.0", description: "高校与企业合作匹配", category: "Education", capabilities: ["university-matching"], tools: ["kg-query"], permissions: ["read:education"], inputSchema: {}, outputSchema: {}, status: "Published" },
    { id: "agent-training-plan", name: "培训计划 Agent", version: "1.0", description: "培训方案生成", category: "Education", capabilities: ["training-planning"], tools: ["ai-model"], permissions: ["read:education"], inputSchema: {}, outputSchema: {}, status: "Published" },
    { id: "agent-career-path", name: "职业路径 Agent", version: "1.0", description: "职业发展路径规划", category: "Talent", capabilities: ["career-planning"], tools: ["ai-model"], permissions: ["read:talent"], inputSchema: {}, outputSchema: {}, status: "Published" },

    // 增长 (8)
    { id: "agent-marketing", name: "营销 Agent", version: "1.0", description: "AI 营销内容生成", category: "Marketing", capabilities: ["content-generation", "campaign-planning"], tools: ["ai-model", "channel-api"], permissions: ["write:marketing"], inputSchema: {}, outputSchema: {}, status: "Published" },
    { id: "agent-sales", name: "销售 Agent", version: "1.0", description: "销售自动化", category: "Sales", capabilities: ["sales-automation", "lead-scoring"], tools: ["crm-api"], permissions: ["write:crm"], inputSchema: {}, outputSchema: {}, status: "Published" },
    { id: "agent-content", name: "内容 Agent", version: "1.0", description: "内容创作与分发", category: "Content", capabilities: ["content-creation", "distribution"], tools: ["ai-model", "channel-api"], permissions: ["write:content"], inputSchema: {}, outputSchema: {}, status: "Published" },
    { id: "agent-seo", name: "SEO Agent", version: "1.0", description: "SEO 优化", category: "Marketing", capabilities: ["seo-optimization"], tools: ["ai-model"], permissions: ["write:content"], inputSchema: {}, outputSchema: {}, status: "Published" },
    { id: "agent-lead-gen", name: "线索生成 Agent", version: "1.0", description: "Lead Generation", category: "Sales", capabilities: ["lead-generation"], tools: ["data-query", "ai-model"], permissions: ["read:market"], inputSchema: {}, outputSchema: {}, status: "Published" },
    { id: "agent-brand", name: "品牌增长 Agent", version: "1.0", description: "品牌传播策略", category: "Marketing", capabilities: ["brand-strategy"], tools: ["ai-model"], permissions: ["read:all"], inputSchema: {}, outputSchema: {}, status: "Published" },
    { id: "agent-user-growth", name: "用户增长 Agent", version: "1.0", description: "用户增长与推荐", category: "Growth", capabilities: ["user-growth", "recommendation"], tools: ["ai-model", "data-query"], permissions: ["read:user"], inputSchema: {}, outputSchema: {}, status: "Published" },
    { id: "agent-conversion", name: "转化优化 Agent", version: "1.0", description: "转化率优化", category: "Growth", capabilities: ["conversion-optimization"], tools: ["data-query", "ai-model"], permissions: ["read:user"], inputSchema: {}, outputSchema: {}, status: "Published" },

    // 生态 (10)
    { id: "agent-ecosystem-match", name: "生态匹配 Agent", version: "1.0", description: "生态对象匹配", category: "Ecosystem", capabilities: ["ecosystem-matching"], tools: ["kg-query", "ai-model"], permissions: ["read:ecosystem"], inputSchema: {}, outputSchema: {}, status: "Published" },
    { id: "agent-project-match", name: "项目匹配 Agent", version: "1.0", description: "项目与资源匹配", category: "Ecosystem", capabilities: ["project-matching"], tools: ["kg-query"], permissions: ["read:project"], inputSchema: {}, outputSchema: {}, status: "Published" },
    { id: "agent-capital-match", name: "资本匹配 Agent", version: "1.0", description: "资本与项目匹配", category: "Investment", capabilities: ["capital-matching"], tools: ["kg-query"], permissions: ["read:investment"], inputSchema: {}, outputSchema: {}, status: "Published" },
    { id: "agent-tech-transfer", name: "技术转移 Agent", version: "1.0", description: "技术转移与商业化", category: "Ecosystem", capabilities: ["tech-transfer"], tools: ["kg-query"], permissions: ["read:technology"], inputSchema: {}, outputSchema: {}, status: "Published" },
    { id: "agent-park-development", name: "园区发展 Agent", version: "1.0", description: "产业园发展策略", category: "Ecosystem", capabilities: ["park-development"], tools: ["ai-model", "digital-twin"], permissions: ["read:ecosystem"], inputSchema: {}, outputSchema: {}, status: "Published" },
    { id: "agent-government-relation", name: "政府关系 Agent", version: "1.0", description: "政策对接", category: "Ecosystem", capabilities: ["government-relations"], tools: ["kg-query"], permissions: ["read:policy"], inputSchema: {}, outputSchema: {}, status: "Published" },
    { id: "agent-channel-development", name: "渠道发展 Agent", version: "1.0", description: "渠道建设", category: "Ecosystem", capabilities: ["channel-development"], tools: ["kg-query"], permissions: ["read:market"], inputSchema: {}, outputSchema: {}, status: "Published" },
    { id: "agent-alliance", name: "联盟建设 Agent", version: "1.0", description: "产业联盟构建", category: "Ecosystem", capabilities: ["alliance-building"], tools: ["kg-query"], permissions: ["read:ecosystem"], inputSchema: {}, outputSchema: {}, status: "Published" },
    { id: "agent-ecosystem-health", name: "生态健康 Agent", version: "1.0", description: "生态健康度监控", category: "Ecosystem", capabilities: ["health-monitoring"], tools: ["data-query"], permissions: ["read:ecosystem"], inputSchema: {}, outputSchema: {}, status: "Published" },
    { id: "agent-ecosystem-recovery", name: "生态修复 Agent", version: "1.0", description: "生态自修复", category: "Ecosystem", capabilities: ["self-repair"], tools: ["ai-model"], permissions: ["read:ecosystem"], inputSchema: {}, outputSchema: {}, status: "Published" },

    // 技术 (8)
    { id: "agent-data-pipeline", name: "数据管道 Agent", version: "1.0", description: "数据采集与处理", category: "Data", capabilities: ["data-ingestion", "etl"], tools: ["data-pipeline"], permissions: ["read:data"], inputSchema: {}, outputSchema: {}, status: "Published" },
    { id: "agent-entity-resolution", name: "实体消歧 Agent", version: "1.0", description: "Entity Resolution", category: "Data", capabilities: ["entity-resolution"], tools: ["ai-model", "kg-query"], permissions: ["read:data"], inputSchema: {}, outputSchema: {}, status: "Published" },
    { id: "agent-knowledge-extraction", name: "知识抽取 Agent", version: "1.0", description: "知识图谱构建", category: "Data", capabilities: ["knowledge-extraction"], tools: ["ai-model", "kg-query"], permissions: ["write:kg"], inputSchema: {}, outputSchema: {}, status: "Published" },
    { id: "agent-ai-tagging", name: "AI 标签 Agent", version: "1.0", description: "实体标签化", category: "Data", capabilities: ["tagging"], tools: ["ai-model"], permissions: ["write:tags"], inputSchema: {}, outputSchema: {}, status: "Published" },
    { id: "agent-ai-profile", name: "AI 画像 Agent", version: "1.0", description: "动态画像生成", category: "Data", capabilities: ["profiling"], tools: ["ai-model"], permissions: ["read:all"], inputSchema: {}, outputSchema: {}, status: "Published" },
    { id: "agent-rag", name: "RAG Agent", version: "1.0", description: "检索增强生成", category: "Technology", capabilities: ["rag", "search"], tools: ["vector-db", "ai-model"], permissions: ["read:all"], inputSchema: {}, outputSchema: {}, status: "Published" },
    { id: "agent-recommendation", name: "推荐 Agent", version: "1.0", description: "产业推荐引擎", category: "Technology", capabilities: ["recommendation"], tools: ["ai-model", "data-query"], permissions: ["read:all"], inputSchema: {}, outputSchema: {}, status: "Published" },
    { id: "agent-monitoring", name: "监控 Agent", version: "1.0", description: "系统监控与告警", category: "Governance", capabilities: ["monitoring", "alerting"], tools: ["metrics"], permissions: ["read:metrics"], inputSchema: {}, outputSchema: {}, status: "Published" },
  ];

  for (const agent of agents) {
    agentRegistry.register(agent);
  }
}